import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Eye, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Calendar,
  User,
  Download
} from 'lucide-react';
import { Button } from '../../components/shared';
import { DatePicker } from '../../components/client';
import DataTable from '../../components/shared/DataTable';
import { useVendorBookings } from '../../services/vendorBookingService';
import { formatDate, formatTime } from '../../utils/dateUtils';

const VendorBookings = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    status: '',
    start_date: '',
    end_date: '',
    service_id: '',
    client_search: '' // Changed from client_id to client_search for text search
  });

  // Clean filters - remove empty values to avoid API errors
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
  );

  // Fetch bookings with filters
  const { data: bookingsResponse, isLoading, error } = useVendorBookings(cleanFilters);
  const stats = bookingsResponse?.statistics || {};
  const bookings = bookingsResponse?.data?.data || [];

  // Define table columns - simplified for overview
  const columns = [
    {
      key: 'booking_reference',
      label: 'Booking #',
      sortable: true,
      width: 'w-56',
      render: (booking) => (
        <span className="font-mono text-sm font-medium text-primary-600">
          #{booking.booking_reference}
        </span>
      )
    },
    {
      key: 'client',
      label: 'Client',
      sortable: false,
      width: 'w-56',
      render: (booking) => (
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
            <User className="w-4 h-4 text-primary-600" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-gray-900 truncate">
              {booking.client?.name || 'Unknown Client'}
            </div>
            <div className="text-sm text-gray-500 truncate">{booking.client?.phone}</div>
          </div>
        </div>
      )
    },
    {
      key: 'service',
      label: 'Service',
      sortable: false,
      width: 'w-56',
      render: (booking) => (
        <div className="min-w-0">
          <div className="font-medium text-gray-900 truncate">
            {booking.vendor_service?.service_name}
          </div>
          <div className="text-sm text-gray-500 truncate">
            {booking.vendor_service?.service_category?.category_name}
          </div>
        </div>
      )
    },
    {
      key: 'event_date',
      label: 'Event Date',
      sortable: true,
      width: 'w-40',
      type: 'date',
      render: (booking) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900">
              {formatDate(booking.event_date, 'medium')}
            </div>
            <div className="text-xs text-gray-500">
              {booking.start_time ? formatTime(booking.start_time) : 'TBD'}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'vendor_payout_amount',
      label: 'Amount',
      sortable: true,
      width: 'w-32',
      type: 'currency',
      render: (booking) => {
        // Helper function to safely format currency
        const formatCurrency = (value) => {
          const num = parseFloat(value);
          if (isNaN(num) || num === null || num === undefined) {
            return '0.00';
          }
          return num.toFixed(2);
        };

        // Calculate service amount received by vendor from completed payments
        // For vendors: service amount = payment amount - platform fee
        const completedPayments = booking.payments?.filter(p => p.status === 'completed') || [];
        const totalServiceAmountReceived = completedPayments.reduce((sum, payment) => {
          const paymentAmount = parseFloat(payment.amount || 0);
          const platformFee = parseFloat(payment.platform_fee || 0);
          const serviceAmount = paymentAmount - platformFee; // Vendor receives amount minus platform fee
          return sum + serviceAmount;
        }, 0);

        // Calculate deposit service amount (for deposit payments only)
        const depositServiceAmount = completedPayments
          .filter(p => p.payment_type === 'deposit')
          .reduce((sum, payment) => {
            const paymentAmount = parseFloat(payment.amount || 0);
            const platformFee = parseFloat(payment.platform_fee || 0);
            return sum + (paymentAmount - platformFee);
          }, 0);

        // Check if only deposit is paid (has deposit payment but not fully paid)
        const hasDepositPayment = completedPayments.some(p => p.payment_type === 'deposit');
        const hasFullPayment = completedPayments.some(p => p.payment_type === 'full_payment');
        const totalDue = parseFloat(booking.subtotal_amount || 0) + parseFloat(booking.platform_fee || 0);
        const isFullyPaid = parseFloat(booking.amount_paid || 0) >= totalDue && totalDue > 0;
        
        // If only deposit is paid, show deposit service amount, otherwise show total received or full payout
        const showDepositAmount = hasDepositPayment && !hasFullPayment && !isFullyPaid;
        const amountToDisplay = showDepositAmount 
          ? depositServiceAmount 
          : (completedPayments.length > 0 
              ? totalServiceAmountReceived 
              : parseFloat(booking.vendor_payout_amount || booking.subtotal_amount || 0));
        
        return (
          <div className="font-medium text-gray-900 text-right">
            ${formatCurrency(amountToDisplay)}
            {showDepositAmount && (
              <div className="text-xs text-gray-500 mt-0.5">Deposit</div>
            )}
          </div>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 'w-36',
      type: 'status',
      render: (booking) => {
        const statusConfig = {
          pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
          confirmed: { color: 'bg-indigo-100 text-indigo-800', icon: CheckCircle },
          in_progress: { color: 'bg-blue-100 text-blue-800', icon: Clock },
          completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
          cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle },
          no_show: { color: 'bg-gray-100 text-gray-800', icon: XCircle }
        };

        const config = statusConfig[booking.status] || statusConfig.pending;
        const Icon = config.icon;

        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color} whitespace-nowrap`}>
            <Icon className="w-3 h-3 mr-1 flex-shrink-0" />
            {booking.status?.replace('_', ' ')}
          </span>
        );
      }
    }
  ];

  // Define table actions
  const actions = [
    {
      label: 'View Details',
      icon: Eye,
      variant: 'default',
      onClick: (booking) => {
        navigate(`/vendor/bookings/${booking.id}`);
      }
    }
  ];

  // Handle status tab / filter change
  const handleStatusTab = (status) => {
    setFilters(prev => ({ ...prev, status }));
  };

  // Handle date range filter
  const handleDateFilter = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      status: '',
      start_date: '',
      end_date: '',
      service_id: '',
      client_search: ''
    });
  };

  // Custom search function
  const handleSearch = (data, searchTerm) => {
    if (!searchTerm) return data;
    
    return data.filter(booking => 
      booking.booking_reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.vendor_service?.service_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.event_location?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Export bookings
  const handleExport = () => {
    console.log('Exporting bookings...');
    // TODO: Implement CSV/Excel export
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Bookings</h3>
            <p className="text-red-600">Failed to load bookings. Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Bookings</h1>
            <p className="mt-2 text-gray-600">
              Manage and track all your bookings
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex space-x-3">
            <Button
              variant="outline"
              icon={Download}
              onClick={handleExport}
            >
              Export
            </Button>
          </div>
        </div>

        {/* Status Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-4 mb-4">
            {[
              { value: '', label: 'All', count: stats.total_bookings || 0 },
              { value: 'pending', label: 'Pending', count: stats.pending_bookings || 0 },
              { value: 'confirmed', label: 'Confirmed', count: stats.confirmed_bookings || 0 },
              { value: 'in_progress', label: 'In Progress', count: stats.in_progress_bookings || 0 },
              { value: 'completed', label: 'Completed', count: stats.completed_bookings || 0 },
              { value: 'cancelled', label: 'Cancelled', count: stats.cancelled_bookings || 0 },
              { value: 'no_show', label: 'No Show', count: stats.no_show_bookings || 0 }
            ].map((tab) => (
              <button
                key={tab.value || 'all'}
                onClick={() => handleStatusTab(tab.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filters.status === tab.value
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-900">Filters</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={clearFilters}
            >
              Clear All
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Start Date Filter */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Start Date
              </label>
              <DatePicker
                value={filters.start_date}
                onChange={(value) => handleDateFilter('start_date', value)}
                placeholder="Select start date"
                className="w-full text-xs sm:text-sm"
              />
            </div>

            {/* End Date Filter */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                End Date
              </label>
              <DatePicker
                value={filters.end_date}
                onChange={(value) => handleDateFilter('end_date', value)}
                placeholder="Select end date"
                className="w-full text-xs sm:text-sm"
              />
            </div>

            {/* Client Filter */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Client
              </label>
              <input
                type="text"
                placeholder="Search by client name..."
                value={filters.client_search}
                onChange={(e) => setFilters(prev => ({ ...prev, client_search: e.target.value }))}
                className="w-full px-3 py-1.5 sm:py-2 border border-gray-300 rounded-md text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>

        {/* Bookings Table */}
        <DataTable
          data={bookings}
          columns={columns}
          actions={actions}
          loading={isLoading}
          searchable={true}
          filterable={false}
          sortable={true}
          selectable={true}
          onSearch={handleSearch}
          searchPlaceholder="Search bookings by reference, client, service, or location..."
          emptyMessage="No bookings found. Start by creating your first service!"
          pageSize={15}
          showPagination={true}
          className="shadow-sm"
        />
      </div>
    </div>
  );
};

export default VendorBookings;
